import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router";

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    LinearProgress,
    Typography,
} from "@mui/material";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import ListItemMenu from "../../../Common/ListItemMenu";
import DevelopmentNeedFormDialog from "./DevelopmentNeedFormDialog";
import DevelopmentNeedStatusDetails from "./DevelopmentNeedStatusDetails";
import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";
import PORTAvatar from "../PORTAvatar";

import { ActionTypes } from "../../../../constants";
import {
    getDevelopmentNeed as getDevelopmentNeedReducer,
    getRmsAppCode,
    getError,
} from "../../../../reducers";
import rmsActions from "../../../../actions/rmsActions";

function DevelopmentNeed({ developmentNeed, onUpdate, onDelete }) {
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState(null);

    const handleCloseDialog = () => setOpenDialog(null);

    const title = developmentNeed?.title ?? "Development need";

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={
                                <PORTAvatar
                                    type={"developmentNeed"}
                                    badge={
                                        !!developmentNeed.pdpId
                                            ? "attached"
                                            : ""
                                    }
                                />
                            }
                            action={
                                <ListItemMenu
                                    onEdit={
                                        developmentNeed?.canUpdate &&
                                        (() => setOpenDialog("update"))
                                    }
                                    onDelete={
                                        developmentNeed?.canDelete &&
                                        (() => setOpenDialog("delete"))
                                    }
                                />
                            }
                            title={
                                <Typography
                                    component="h2"
                                    className="card-header-title"
                                >
                                    {title}
                                </Typography>
                            }
                            subheader={
                                <Typography
                                    component="span"
                                    className="card-header-subtitle"
                                >
                                    Development need
                                </Typography>
                            }
                        />

                        <CardContent>
                            <Box className="card-content">
                                <DevelopmentNeedStatusDetails
                                    status={developmentNeed?.status}
                                    dateCompletion={
                                        developmentNeed?.dateCompletion
                                    }
                                    dateReview={developmentNeed?.dateReview}
                                    dateEnd={developmentNeed?.dateEnd}
                                />
                            </Box>
                            <Box className="card-content">
                                <Typography variant="body1">
                                    {developmentNeed?.description ?? ""}
                                </Typography>
                            </Box>
                        </CardContent>

                        <CardContent>
                            <Box className="card-content">
                                <Typography variant="h3" component="h3" mb={1}>
                                    How will you address this development need?
                                </Typography>
                                <Typography variant="body1">
                                    {developmentNeed?.planOfAction ?? ""}
                                </Typography>
                            </Box>
                        </CardContent>
                        <CardContent>
                            <Box className="card-content">
                                <Typography variant="h3" component="h3" mb={1}>
                                    What resources do you need (if any)?
                                </Typography>
                                <Typography variant="body1">
                                    {developmentNeed?.resourcesRequired ?? ""}
                                </Typography>
                            </Box>
                        </CardContent>
                        <CardContent>
                            <Box className="card-content">
                                <Typography variant="h3" component="h3" mb={1}>
                                    How do you anticipate this will change your
                                    work?
                                </Typography>
                                <Typography variant="body1">
                                    {developmentNeed?.proposedOutcome ?? ""}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {openDialog === "delete" && (
                <ConfirmDialog
                    open={true}
                    title="Delete development need"
                    content={`The following development need will be deleted: ${developmentNeed.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => {
                        onDelete(developmentNeed.id);
                        history.replace("/portfolio/developmentneeds");
                    }}
                    onCancel={handleCloseDialog}
                    onClose={handleCloseDialog}
                />
            )}

            {openDialog === "update" && (
                <DevelopmentNeedFormDialog
                    open={true}
                    formTitle="Edit development need"
                    data={developmentNeed}
                    onSave={(params) => onUpdate(developmentNeed.id, params)}
                    onClose={handleCloseDialog}
                    scroll="paper"
                    submitLabel="Save"
                />
            )}
        </Container>
    );
}

const DevelopmentNeedContainer = ({
    developmentNeed,
    error,
    match,
    getDevelopmentNeed,
    updateDevelopmentNeed,
    deleteDevelopmentNeed,
    appCode,
}) => {
    useEffect(() => {
        getDevelopmentNeed(match.params.id, appCode);
    }, [getDevelopmentNeed, match.params.id, appCode]);

    if (!!error) {
        if (error.errorCode === 404) {
            return <Error404 />;
        }
        if (error.errorCode === 500) {
            return <Error500 />;
        }
    }

    if (!developmentNeed) {
        return <LinearProgress />;
    }

    return (
        <DevelopmentNeed
            developmentNeed={developmentNeed}
            onUpdate={(id, params) =>
                updateDevelopmentNeed(appCode, id, params)
            }
            onDelete={(id) => deleteDevelopmentNeed(appCode, id)}
        />
    );
};

const mapStateToProps = (state, { match }) => ({
    developmentNeed: getDevelopmentNeedReducer(state, match.params.id),
    error: getError(state, ActionTypes.GET_DEVELOPMENT_NEED_SUCCESS),
    appCode: getRmsAppCode(state),
});

const mapDispatchToProps = {
    getDevelopmentNeed: rmsActions.getDevelopmentNeed,
    updateDevelopmentNeed: rmsActions.updateDevelopmentNeed,
    deleteDevelopmentNeed: rmsActions.deleteDevelopmentNeed,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DevelopmentNeedContainer);
